import { HousingOwnership, ResidenceType, type UpsertResidenceInput } from '../../../__generated__/globalTypes';
import { isCompleteAddress, type IResidenceAddress } from '../../../lib/dependents/residenceAddress';

import type { IDependentResidences, IResidence } from '../../../hooks/queries/useDependentResidences';

// ---------------------------------------------------------------------------
// Residence sub-form state + pure logic for the dependents settings form (the
// onboarding residence step has its own model in `steps/residence/`). Holds
// the "lives with you" answer, the conditionally-revealed primary-address
// fields, and the residence-type / home-ownership cascade — plus the hydrate /
// build / validate helpers that translate that state to and from
// `upsertResidence` inputs.
// ---------------------------------------------------------------------------

export interface IFrequentPlace {
  /** `Residence.id` of the existing row to upsert into, or `null` for new entries. */
  id: string | null;
  /** Controlled input text. */
  text: string;
  /** Structured pick — `null` until the user selects a suggestion. */
  address: IResidenceAddress | null;
  /**
   * For an unsaved row, the dependent card it was added on — that card alone
   * renders it until a save persists it into the household-shared list.
   * Absent on hydrated rows and untagged drafts (those render on every card).
   */
  draftFor?: string;
}

export interface IResidenceFormState {
  /** Existing primary `Residence.id` for this group, or `null` if none yet. Threaded through to `upsertResidence` so a re-submit updates the row. */
  primaryResidenceId: string | null;
  liveWithCaregiver: boolean | null;
  /** Controlled address-input text. */
  addressText: string;
  /** Structured pick — `null` until the user selects a full-address suggestion. */
  address: IResidenceAddress | null;
  /** `null` until the user picks one — the Select shows its placeholder. */
  residenceType: ResidenceType | null;
  residenceTypeOther: string;
  homeOwnership: HousingOwnership | null;
  homeOwnershipOther: string;
}

/**
 * Shared "frequently spend time elsewhere" state. Persisted as family-shared
 * `Residence` rows (owner null) — the question is asked once across the
 * household, so the rows are never pinned to an individual dependent.
 *
 * Removals are NOT tracked here — consumers compute a real diff against the
 * hydration snapshot at submit time, so anything that started in the DB and
 * isn't in `places` (or the toggle is `false`) gets a `deleteResidence` call
 * automatically.
 */
export interface IFrequentLocationsState {
  toggle: boolean | null;
  /** Free-text addresses, one per row; first row is rendered as soon as the toggle flips on. */
  places: IFrequentPlace[];
}

export const INITIAL_FORM_STATE: IResidenceFormState = {
  primaryResidenceId: null,
  liveWithCaregiver: null,
  addressText: '',
  address: null,
  residenceType: null,
  residenceTypeOther: '',
  homeOwnership: null,
  homeOwnershipOther: '',
};

export const INITIAL_FREQUENT_LOCATIONS_STATE: IFrequentLocationsState = {
  toggle: null,
  places: [],
};

/** Map an existing persisted residence into the form's address shape. */
const residenceToAddress = (r: IResidence): IResidenceAddress => ({
  formatted: r.formatted ?? r.addressLine1 ?? r.city,
  addressLine1: r.addressLine1 ?? null,
  addressLine2: r.addressLine2 ?? null,
  city: r.city,
  state: r.state ?? null,
  postalCode: r.postalCode ?? null,
  country: r.country,
  placeId: r.placeId ?? null,
});

/**
 * Pre-fill a single group's form from existing data. The `liveWithCaregiver`
 * answer is read from the persisted `caregiver_relationships.lives_with_caregiver`
 * flag — NOT inferred from residence presence, because a "lives with you = Yes"
 * answer intentionally creates no residence row.
 *
 * The address/living-situation fields are only adopted when the answer is an
 * explicit `false` AND the resolved primary is this dependent's OWN residence.
 * The server resolves a dependent's `primary` through cohabitant/caregiver
 * chains (see ResidenceController), so a primary owned by the caregiver or a
 * partner must never be written back as this dependent's own residence —
 * doing so would reparent another member's row on submit.
 */
export const hydrateFormState = (
  dependentId: string,
  livesWithCaregiver: boolean | null,
  dependentResidence: IDependentResidences | null
): IResidenceFormState => {
  const primary = dependentResidence?.primary ?? null;
  const ownsPrimary = primary !== null && primary.ownerKind === 'dependent' && primary.ownerId === dependentId;
  if (livesWithCaregiver === false && ownsPrimary) {
    return {
      primaryResidenceId: primary.id,
      liveWithCaregiver: false,
      addressText: primary.formatted ?? primary.addressLine1 ?? '',
      address: residenceToAddress(primary),
      residenceType: primary.residenceType,
      residenceTypeOther: primary.residenceTypeOther ?? '',
      homeOwnership: primary.ownership ?? null,
      homeOwnershipOther: primary.ownershipOther ?? '',
    };
  }
  return { ...INITIAL_FORM_STATE, liveWithCaregiver: livesWithCaregiver };
};

/**
 * Pre-fill the shared frequent-locations state by aggregating each dependent's
 * `other` residences into one deduped list. The same `Residence` id can appear
 * under multiple dependents (family-shared rows); the Map collapses those into
 * a single entry.
 */
export const hydrateFrequentLocations = (
  dependentResidences: readonly IDependentResidences[]
): IFrequentLocationsState => {
  const byId = new Map<string, IResidence>();
  for (const { other } of dependentResidences) {
    for (const r of other) {
      if (!byId.has(r.id)) {
        byId.set(r.id, r);
      }
    }
  }
  if (byId.size === 0) {
    return INITIAL_FREQUENT_LOCATIONS_STATE;
  }
  return {
    toggle: true,
    places: [...byId.values()].map((r) => ({
      id: r.id,
      text: r.formatted ?? r.addressLine1 ?? r.city,
      address: residenceToAddress(r),
    })),
  };
};

// ---------------------------------------------------------------------------
// Build — translate form state into `upsertResidence` inputs. A primary is
// emitted only when the group does NOT live with the caregiver AND a full
// address has been picked (or hydrated), so a null address never produces a
// half-filled row. Ownership pointers (`userId` / `dependantId`) are create-only on
// the wire — ignored in the update path.
//
// The DB schema allows at-most-one of `userId`/`dependantId` per row, so a couple
// living together can't co-own one residence — the caller owns the primary by
// the first dependent (see ORN-868 for the polymorphic owner upgrade).
// ---------------------------------------------------------------------------

interface IBuildInputsArgs {
  pathBForms?: { dependentId: string; form: IResidenceFormState }[];
  pathAForm?: { dependentId: string; form: IResidenceFormState };
}

export const buildResidenceInputs = ({ pathAForm, pathBForms }: IBuildInputsArgs): UpsertResidenceInput[] => {
  const groups = pathAForm ? [pathAForm] : (pathBForms ?? []);
  const inputs: UpsertResidenceInput[] = [];

  for (const { dependentId, form } of groups) {
    if (form.liveWithCaregiver === false && form.address !== null && form.residenceType !== null) {
      const addr = form.address;
      inputs.push({
        residenceId: form.primaryResidenceId,
        dependantId: dependentId,
        userId: null,
        isPrimary: true,
        residenceType: form.residenceType,
        residenceTypeOther: form.residenceType === ResidenceType.Other ? form.residenceTypeOther.trim() || null : null,
        addressLine1: addr.addressLine1,
        addressLine2: addr.addressLine2,
        city: addr.city,
        state: addr.state,
        postalCode: addr.postalCode,
        country: addr.country,
        placeId: addr.placeId,
        formatted: addr.formatted,
        ownership: form.residenceType === ResidenceType.IndependentLiving ? form.homeOwnership : null,
        ownershipOther: form.homeOwnership === HousingOwnership.Other ? form.homeOwnershipOther.trim() || null : null,
      });
    }
  }

  return inputs;
};

/**
 * Translate the shared frequent-locations state into 0..N non-primary
 * `Residence` inputs. Frequent locations are always family-shared (owner null).
 * Returns `[]` when the toggle is anything other than `true`, even if the user
 * typed into a row before flipping back.
 */
export const buildFrequentPlaceInputs = (state: IFrequentLocationsState): UpsertResidenceInput[] => {
  if (state.toggle !== true) {
    return [];
  }
  const inputs: UpsertResidenceInput[] = [];
  for (const place of state.places) {
    const addr = place.address;
    // Defensive: `isFrequentLocationsReady` already blocks on a city-less pick,
    // so an incomplete row here means a code path slipped past the gate — never
    // emit an empty `city` to the wire regardless.
    if (!isCompleteAddress(addr)) {
      continue;
    }
    inputs.push({
      residenceId: place.id,
      dependantId: null,
      userId: null,
      isPrimary: false,
      residenceType: ResidenceType.FrequentLocation,
      residenceTypeOther: null,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2,
      city: addr.city,
      state: addr.state,
      postalCode: addr.postalCode,
      country: addr.country,
      placeId: addr.placeId,
      formatted: addr.formatted,
      ownership: null,
      ownershipOther: null,
    });
  }
  return inputs;
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const isFormReady = (state: IResidenceFormState): boolean => {
  if (state.liveWithCaregiver === null) {
    return false;
  }
  if (state.liveWithCaregiver === false) {
    // A primary residence must be a full street address — `addressLine1` is
    // only populated for street-level Places picks (or a hydrated row) — and
    // must carry a city/country like any other residence.
    if (!isCompleteAddress(state.address) || state.address?.addressLine1 == null) {
      return false;
    }
    if (!isResidenceCascadeComplete(state)) {
      return false;
    }
  }
  return true;
};

/**
 * The residence-type cascade is complete: a type is chosen, an `Other` type has
 * its describe field, and independent living has an ownership selection (with
 * its own describe when ownership is `Other`). Used by the hard `isFormReady`
 * gate and the settings soft gate (which only enforces it once an actual
 * upsert would be produced).
 */
export const isResidenceCascadeComplete = (state: IResidenceFormState): boolean => {
  if (state.residenceType === null) {
    return false;
  }
  if (state.residenceType === ResidenceType.Other && state.residenceTypeOther.trim().length === 0) {
    return false;
  }
  if (state.residenceType === ResidenceType.IndependentLiving) {
    if (state.homeOwnership === null) {
      return false;
    }
    if (state.homeOwnership === HousingOwnership.Other && state.homeOwnershipOther.trim().length === 0) {
      return false;
    }
  }
  return true;
};

export const isFrequentLocationsReady = (state: IFrequentLocationsState): boolean => {
  if (state.toggle === null) {
    return false;
  }
  if (state.toggle === true) {
    // At least one real pick, and every pick must resolve to a city/country — a
    // row the user picked but that came back city-less blocks submit so it
    // can't be silently dropped (see `isCompleteAddress`).
    const picked = state.places.filter((p) => p.address !== null);
    return picked.length > 0 && picked.every((p) => isCompleteAddress(p.address));
  }
  return true;
};
